/**
 * API configuration utilities.
 * Single source for API base URL and request headers (used by questionnaire and contract_review).
 */

import { applyAuthErrorHandlers } from './apiErrors';
import { refreshIfNeeded, isAuthenticated, updateLastActive } from '../auth/okta';

/**
 * Get the API base URL from environment variable or use defaults.
 * @returns API base URL string (no trailing slash)
 */
export const getApiUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    const url = process.env.REACT_APP_API_URL;
    return url.replace(/\/$/, '');
  }
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8080';
  }
  // On any deployed environment (Cloud Run, gateway) use relative URLs so all
  // /api/* requests go to the same origin (the gateway), which proxies them to
  // the backend with a GCP identity token.
  return '';
};

/**
 * Default headers for API requests (JSON Content-Type + optional Bearer token).
 * For multipart uploads, do not set Content-Type; let the browser set it.
 */
export const getApiHeaders = (accessToken?: string | null): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getApiAuthHeaders(accessToken),
  };
  return headers;
};

/**
 * Bearer token only (no Content-Type). Use for multipart uploads and axios defaults
 * so JSON Content-Type is not forced on FormData requests.
 */
export const getApiAuthHeaders = (accessToken?: string | null): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  return headers;
};

/**
 * Run centralized 401/403 handling for a fetch Response. Safe to call before reading the body.
 * @throws Error if status was 401 or 403 (after handlers run)
 */
export function throwIfAuthErrorResponse(res: Response): void {
  if (applyAuthErrorHandlers(res.status)) {
    throw new Error(res.status === 401 ? 'Unauthorized' : 'Forbidden');
  }
}

/**
 * Single pre-request hook for every backend call, regardless of HTTP client
 * (fetch via apiFetch, axios via the interceptor in index.tsx, or any future
 * client) — stamps activity and proactively refreshes a near-expiry token.
 * Cross-cutting request behavior must be added here, not per-client, so it
 * can't silently miss a call path the way updateLastActive once did for axios.
 */
export async function onBeforeApiRequest(): Promise<void> {
  if (!isAuthenticated()) return;
  updateLastActive();
  await refreshIfNeeded();
}

/**
 * Fetch with central 401/403 handling (admin API, etc.).
 * Proactively refreshes the access token when it is within 2 minutes of expiry
 * so the outgoing request always carries a valid token.
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  await onBeforeApiRequest();
  const res = await fetch(input, init);
  throwIfAuthErrorResponse(res);
  return res;
}

/**
 * After storing tokens (Okta callback or dev login), call backend to provision the DB user.
 * Non-fatal if it fails (e.g. network); /api/me or other calls will retry provisioning.
 */
export async function syncAuthSessionAfterLogin(accessToken: string): Promise<void> {
  if (!accessToken) return;
  try {
    await fetch(`${getApiUrl()}/api/auth/session`, {
      headers: getApiHeaders(accessToken),
    });
  } catch {
    // ignore — user will be created on next authenticated request
  }
}
